/**
 * PRODUCT IMAGES CUSTOM ELEMENT
 * 
 * Handles interactive functionality for stroller product images:
 * - Mobile slider with touch/swipe navigation
 * - Video play/pause controls
 * - Desktop hover zoom with mouse position tracking
 * 
 * Features:
 * - Touch-enabled mobile slider
 * - Dot navigation with thumbnails
 * - Custom video controls
 * - Mouse position-based zoom on desktop
 */

if (!customElements.get('product-images')) {
  customElements.define('product-images', class ProductImages extends HTMLElement {
    constructor() {
      super();
      this.sliderInitialized = false; // Track if Slick slider is initialized
      this.isPlaying = false; // Track video playing state (reserved for future use)
      this.onExternalVariantChange = this.onExternalVariantChange?.bind(this) || this.onExternalVariantChange;
    }

    /**
     * INITIALIZATION - Called when element is added to DOM
     * Sets up all interactive functionality
     */
    connectedCallback() {
      // Wait for dependencies (jQuery, Slick) to be loaded
      this.initWhenReady();
      this.setupVideoHandlers(); // Video play/pause controls
      this.setupDesktopZoom();   // Desktop hover zoom
      // Ensure mobile slider positions correctly when variant changes reveal this image set
      document.addEventListener('variant:change', this.onExternalVariantChange);
    }

    /**
     * Wait for dependencies and initialize slider
     */
    initWhenReady() {
      // Check if jQuery and Slick are available
      if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick !== 'undefined') {
        this.setupSlickSlider();
      } else {
        // Retry after a short delay if dependencies aren't loaded yet
        setTimeout(() => this.initWhenReady(), 100);
      }
    }

    disconnectedCallback() {
      this.destroySlider();
      document.removeEventListener('variant:change', this.onExternalVariantChange);
      
      // Clean up visibility observer
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }
    }

    /**
     * SLICK SLIDER SETUP  
     * Initializes Slick slider for mobile with custom dot navigation
     */
    setupSlickSlider() {
      const mainSlider = this.querySelector('.stroller-product-images__slider-main');
      const dotsContainer = this.querySelector('.stroller-product-images__dots');
      
      // Only initialize slider on mobile
      if (!mainSlider || window.innerWidth > 768) {
        return;
      }

      // Skip if already initialized
      if ($(mainSlider).hasClass('slick-initialized')) {
        // If hidden, set up observer to refresh when visible
        if (this.offsetParent === null) {
          this.observeVisibility();
        }
        return;
      }

      // Initialize Slick Slider for mobile (even if hidden - it will be shown by CSS)
      $(mainSlider).slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        dots: false, // We'll use custom dots
        infinite: true,
        speed: 300,
        cssEase: 'ease-in-out',
        adaptiveHeight: false,
        swipe: true,
        touchMove: true
      });

      this.sliderInitialized = true;

      // Setup custom thumbnail dots navigation
      if (dotsContainer) {
        this.setupCustomDots(mainSlider, dotsContainer);
      }

      // If this slider is hidden, set up observer to refresh when it becomes visible
      if (this.offsetParent === null) {
        this.observeVisibility();
      }
    }

    /**
     * Observe visibility changes and refresh slider when element becomes visible
     */
    observeVisibility() {
      // Don't create multiple observers
      if (this.visibilityObserver) {
        return;
      }

      // Use MutationObserver to watch for style/display changes on parent elements
      this.visibilityObserver = new MutationObserver(() => {
        const mainSlider = this.querySelector('.stroller-product-images__slider-main');
        
        // Check if now visible
        if (this.offsetParent !== null && mainSlider) {
          // Refresh slider position
          if ($(mainSlider).hasClass('slick-initialized')) {
            $(mainSlider).slick('setPosition');
          }
          
          // Stop observing once visible
          if (this.visibilityObserver) {
            this.visibilityObserver.disconnect();
            this.visibilityObserver = null;
          }
        }
      });

      // Observe the element and its parents for attribute/style changes
      this.visibilityObserver.observe(this.closest('.stroller-main-product') || document.body, {
        attributes: true,
        attributeFilter: ['data-variant-selected', 'style', 'class'],
        subtree: true
      });
    }

    onExternalVariantChange = () => {
      setTimeout(() => {
        this.ensureMobileSliderPosition();
      }, 0);
    };

    ensureMobileSliderPosition() {
      const mainSlider = this.querySelector('.stroller-product-images__slider-main');
      if (!mainSlider || window.innerWidth > 768) return;

      if ($(mainSlider).hasClass('slick-initialized')) {
        $(mainSlider).slick('setPosition');
        $(mainSlider).slick('slickGoTo', 0, true);
      } else {
        this.setupSlickSlider();
      }
    }

    /**
     * SETUP CUSTOM THUMBNAIL DOTS
     * Creates custom dot navigation using thumbnail images
     */
    setupCustomDots(slider, dotsContainer) {
      const dots = dotsContainer.querySelectorAll('.stroller-product-images__dot');
      
      // Set first dot as active
      if (dots.length > 0) {
        dots[0].classList.add('stroller-product-images__dot--active');
      }

      // Handle dot clicks
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          $(slider).slick('slickGoTo', index);
        });
      });

      // Update active dot when slide changes
      $(slider).on('beforeChange', (event, slick, currentSlide, nextSlide) => {
        // Remove active class from all dots
        dots.forEach(dot => {
          dot.classList.remove('stroller-product-images__dot--active');
        });
        
        // Add active class to current dot
        if (dots[nextSlide]) {
          dots[nextSlide].classList.add('stroller-product-images__dot--active');
        }
      });
    }

    /**
     * DESTROY SLIDER
     * Clean up Slick instance when element is removed
     */
    destroySlider() {
      const mainSlider = this.querySelector('.stroller-product-images__slider-main');
      if (mainSlider && this.sliderInitialized && $(mainSlider).hasClass('slick-initialized')) {
        $(mainSlider).slick('unslick');
        this.sliderInitialized = false;
      }
    }

    /**
     * VIDEO CONTROL SETUP
     * Handles custom play/pause functionality for videos
     * Replaces default video controls with custom button
     */
    setupVideoHandlers() {
      const playButtons = this.querySelectorAll('.stroller-product-images__play-btn');
      
      // Setup click handlers for custom play buttons
      playButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault(); // Prevent default button behavior
          
          // Find associated video element
          const wrapper = button.closest('.stroller-product-images__media-wrapper');
          const video = wrapper.querySelector('video');
          
          if (video) {
            // Toggle play/pause state
            if (video.paused) {
              video.play();
              button.style.display = 'none'; // Hide button during playback
            } else {
              video.pause();
              button.style.display = 'block'; // Show button when paused
            }
          }
        });
      });

      // AUTOMATIC BUTTON VISIBILITY CONTROL
      // Hide/show play button based on video state
      const videos = this.querySelectorAll('video');
      videos.forEach(video => {
        // Hide button when video starts playing
        video.addEventListener('play', () => {
          const button = video.parentElement.querySelector('.stroller-product-images__play-btn');
          if (button) button.style.display = 'none';
          // Show native controls once video starts playing
          video.setAttribute('controls', 'controls');
        });
        
        // Show button when video is paused
        video.addEventListener('pause', () => {
          const button = video.parentElement.querySelector('.stroller-product-images__play-btn');
          if (button) button.style.display = 'block';
          // Hide native controls when paused
          video.removeAttribute('controls');
        });
      });
    }

    /**
     * DESKTOP HOVER ZOOM SETUP
     * Implements mouse position-based zoom for images on desktop
     * Only active on screens > 768px
     */
    setupDesktopZoom() {
      // Skip if on mobile/tablet
      if (window.innerWidth <= 768) return;
      
      // Only apply to grid layout (desktop), not slider
      const mediaWrappers = this.querySelectorAll('.stroller-product-images__grid .stroller-product-images__media-wrapper');
      
      mediaWrappers.forEach(wrapper => {
        const media = wrapper.querySelector('.stroller-product-images__media');
        
        // Only apply zoom to images, not videos
        if (!media || media.tagName === 'VIDEO') return;

        // MOUSE MOVE - Update zoom origin based on cursor position
        wrapper.addEventListener('mousemove', (e) => {
          const rect = wrapper.getBoundingClientRect();
          
          // Calculate mouse position as percentage within element
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          
          // Set zoom origin to mouse position for natural zoom behavior
          media.style.transformOrigin = `${x}% ${y}%`;
        });

        // MOUSE LEAVE - Reset zoom origin to center
        wrapper.addEventListener('mouseleave', () => {
          media.style.transformOrigin = 'center center';
        });
      });
    }
  });
}

// Handle window resize to reinitialize Slick if needed
window.addEventListener('resize', function() {
  // Debounce resize handler
  clearTimeout(window.strollerResizeTimeout);
  window.strollerResizeTimeout = setTimeout(function() {
    const strollerImages = document.querySelectorAll('product-images');
    
    strollerImages.forEach(function(container) {
      const mainSlider = container.querySelector('.stroller-product-images__slider-main');
      
      if (mainSlider && $(mainSlider).hasClass('slick-initialized')) {
        if (window.innerWidth > 768) {
          // Destroy slider on desktop
          $(mainSlider).slick('unslick');
          container.sliderInitialized = false;
        }
      } else if (mainSlider && window.innerWidth <= 768 && !container.sliderInitialized) {
        // Initialize slider on mobile if not already initialized
        container.setupSlickSlider();
      }
    });
  }, 250);
});
